import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties, PointerEvent, ReactNode } from "react";
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from "pdfjs-dist";
import {
  CHOICE_TEXT,
  COURSE_MARKS,
  DEFAULT_TRANSLATION,
  TRANSLATION_SMALLER,
  translationOf,
  DISPLAY_KEYS,
  fontCuts,
  isDisplayFont,
  SET_LAYOUTS,
  choiceTextOf,
  DESIGN_COLORS,
  DESIGN_FONTS,
  DISH_SPACE,
  LINE_SPACINGS,
  NUMBER_COLUMN,
  OR_TEXT,
  orTextOf,
  FOOT_LOGO_PLACES,
  FOOT_LOGO_SIZE,
  HEAD_WIDTH,
  LOGO_SIZE,
  VARIANT_INDENT,
  MARGIN_X,
  MARGIN_Y,
  PAGE_FORMATS,
  PRESETS,
  PRICE_COLUMN,
  SECTION_SPACE,
  SIZE_LIMITS,
  DRINK_STYLE_KEYS,
  SET_DRINK_STYLE_KEYS,
  drinkDesign,
  drinkEuroOf,
  drinkStylesSame,
  setDrinkStylesSame,
  STYLE_GROUPS,
  STYLE_LABELS,
  TRACKINGS,
  fontOf,
  cssFamily,
  cssVariant,
  SMALL_CAPS_FONTS,
  lineSpacingOf,
} from "@speisekarte/core";
import type { CardLang, Caps, CourseMark, Design, TranslationRule, DrinkStyleKey, FootLogoPlace, LogoPlace, MenuInput, PresetId, SetDrinkStyleKey, StyleKey, TextStyle } from "@speisekarte/core";
import { heldLogo, logoPng } from "../logo.ts";
import { printMenuOf } from "../output.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import type { Mark } from "../menuPdf.ts";
import { WordStyles } from "./WordStyles.tsx";

/** Every free font once, so the samples and the list of fonts show in their own letters. */
const FACES = DESIGN_FONTS.filter((f) => f.file)
  .flatMap((f) => [400, 700].flatMap((w) => ["normal", "italic"].map((s) => `@font-face { font-family: "${f.label}"; font-weight: ${w}; font-style: ${s}; font-display: swap; src: url("/fonts/${f.file}-${w}-${s}.woff") format("woff"); }`)))
  // Real small capitals (T9).
  .concat(Object.values(SMALL_CAPS_FONTS).map((f) => `@font-face { font-family: "${f.label}"; font-weight: 400; font-style: normal; font-display: swap; src: url("/fonts/${f.file}") format("woff"); }`))
  .join("\n");

const cssOf = (s: TextStyle): CSSProperties => ({
  fontFamily: cssFamily(s),
  fontSize: `${Math.min(s.size, 16) * 1.33}px`,
  color: s.color,
  fontWeight: s.bold ? 700 : 400,
  fontStyle: s.italic ? "italic" : "normal",
  textTransform: s.caps === "upper" ? "uppercase" : "none",
  fontVariant: cssVariant(s),
  letterSpacing: `${s.tracking / 100}em`,
});

const pt = (n: number) => `${String(n).replace(".", ",")} pt`;
const mmText = (n: number) => `${String(n).replace(".", ",")} mm`;
/** The three ways a head can look (Martin, 24.10.2026): no logo, the logo above the text, the text beside the logo. */
type HeadLayout = "none" | "center" | "left" | "right";
const HEAD_LAYOUTS: { id: HeadLayout; label: string }[] = [
  { id: "none", label: "Ohne Logo" },
  { id: "center", label: "Logo über dem Text" },
  { id: "left", label: "Logo links, Text daneben" },
  { id: "right", label: "Logo rechts, Text daneben" },
];

/** A little drawing of a head: the logo as a square, the lines of text as bars. */
function HeadSketch({ layout, align }: { layout: HeadLayout; align: "center" | "left" }) {
  const bars = (x: number, w: number, left: boolean) =>
    [10, 6, 8].map((bw, i) => <rect key={i} x={left ? x : x + (w - bw * 3) / 2} y={(layout === "center" ? 26 : 12) + i * 7} width={bw * 3} height={i === 0 ? 4 : 3} rx={1} className="bar" />);
  if (layout === "left" || layout === "right") {
    const logoX = layout === "left" ? 6 : 42;
    return (
      <svg viewBox="0 0 64 44" aria-hidden="true">
        <rect x={logoX} y={10} width={16} height={22} rx={2} className="logo" />
        {bars(layout === "left" ? 26 : 6, 34, true)}
      </svg>
    );
  }
  const left = align === "left";
  return (
    <svg viewBox="0 0 64 44" aria-hidden="true">
      {layout === "center" && <rect x={left ? 6 : 24} y={4} width={16} height={18} rx={2} className="logo" />}
      {bars(6, 52, left)}
    </svg>
  );
}

/** „Logo über dem Text · 80 %“: the head's layout and the width of its text, for the closed entry; „Logo 200 %“ when not as usual. */
const headSummary = (d: Design, hasLogo: boolean) => {
  const layout: HeadLayout = !hasLogo || d.logoHidden ? "none" : d.logoPlace;
  const size = layout !== "none" && d.logoSize !== undefined && d.logoSize !== LOGO_SIZE.fallback ? ` · Logo ${d.logoSize} %` : "";
  return `${HEAD_LAYOUTS.find((l) => l.id === layout)!.label.replace(", Text daneben", "")}${size}${hasLogo ? footLogoSummary(d) : ""} · ${d.headWidth} %`;
};

const FOOT_LOGO_LABELS: Record<FootLogoPlace, string> = { left: "links", center: "mittig", right: "rechts" };

/** „ · im Fuß rechts“: the logo in the foot (S43) in the closed entry of the head; nothing without. */
const footLogoSummary = (d: Design) => (d.footLogo ? ` · im Fuß ${FOOT_LOGO_LABELS[d.footLogo]}` : "");

/**
 * „Logo im Fuß“ (S43): the logo once more at the bottom of each page that has none in the head –
 * all pages after the first, or all of them when the head shows no logo (a daily insert with the
 * logo only at its foot). Print, PDF and Word.
 */
function FootLogoChoice({ design, change }: { design: Design; change: (fn: (d: Design) => Design) => void }) {
  const choose = (place: FootLogoPlace | null) =>
    change((d) => {
      const { footLogo: _place, footLogoSize: _size, ...rest } = d;
      return place ? { ...rest, footLogo: place, ...(d.footLogoSize ? { footLogoSize: d.footLogoSize } : {}) } : rest;
    });
  return (
    <div className="foot-logo" data-testid="foot-logo">
      <h4>Logo im Fuß der Seiten</h4>
      <div className="row">
        <span>Im Fuß</span>
        <span className="segmented small">
          <button className={design.footLogo ? "" : "active"} onClick={() => choose(null)} data-testid="foot-logo-off">
            aus
          </button>
          {FOOT_LOGO_PLACES.map((f) => (
            <button key={f} className={design.footLogo === f ? "active" : ""} onClick={() => choose(f)} data-testid={`foot-logo-${f}`}>
              {FOOT_LOGO_LABELS[f]}
            </button>
          ))}
        </span>
      </div>
      {design.footLogo && <Slider label="Größe im Fuß" value={design.footLogoSize ?? FOOT_LOGO_SIZE.fallback} limits={FOOT_LOGO_SIZE} step={10} show={(n) => `${n} %`} onChange={(v) => change((d) => ({ ...d, footLogoSize: v }))} testid="foot-logo-size" />}
      <p className="hint">Unten auf jeder Seite ohne Logo im Kopf: ab Seite 2, oder auf allen, wenn der Kopf „Ohne Logo“ zeigt (etwa auf einem Einlegeblatt). In Druck, PDF und Word.</p>
    </div>
  );
}

/**
 * „Logo- und Textanzeige“, the first entry of „Kopf“: one choice for the layout, then only what
 * belongs to it – the alignment (without a logo or with it above, logo and text together), „Logo
 * statt Name“ (with a logo), the width of the text.
 */
function HeadChoice({ design, change, hasLogo }: { design: Design; change: (fn: (d: Design) => Design) => void; hasLogo: boolean }) {
  const layout: HeadLayout = !hasLogo || design.logoHidden ? "none" : design.logoPlace;
  const choose = (l: HeadLayout) => change((d) => (l === "none" ? { ...d, logoHidden: true } : { ...d, logoHidden: false, logoPlace: l as LogoPlace }));
  const side = layout === "left" || layout === "right";
  return (
    <div className="head-choice">
      <div className="head-layouts" role="radiogroup" aria-label="Aufbau des Kopfs">
        {HEAD_LAYOUTS.map((l) => (
          <button key={l.id} role="radio" aria-checked={layout === l.id} className={layout === l.id ? "active" : ""} disabled={l.id !== "none" && !hasLogo} onClick={() => choose(l.id)} data-testid={`logo-${l.id}`}>
            <HeadSketch layout={l.id} align={design.headAlign} />
            <span>{l.label}</span>
          </button>
        ))}
      </div>
      {!hasLogo && <p className="hint">Noch kein Logo: hochladen ganz unten unter „Logo ändern“.</p>}
      {!side && (
        <div className="row">
          <span>{layout === "center" ? "Logo und Text" : "Text"}</span>
          <span className="segmented small">
            {(["center", "left"] as const).map((a) => (
              <button key={a} className={design.headAlign === a ? "active" : ""} onClick={() => change((d) => ({ ...d, headAlign: a }))} data-testid={`head-${a}`}>
                {a === "center" ? "mittig" : "linksbündig"}
              </button>
            ))}
          </span>
        </div>
      )}
      {side && <p className="hint">Der Text steht neben dem Logo, linksbündig. Schmaler gestellt rückt er an den {layout === "left" ? "rechten" : "linken"} Rand, der Platz bleibt zum Logo hin.</p>}
      {layout !== "none" && (
        <label className="check">
          <input type="checkbox" checked={design.logoOnly} onChange={(e) => change((d) => ({ ...d, logoOnly: e.target.checked }))} data-testid="logo-only" />
          Logo statt Name (der Name steht schon im Logo)
        </label>
      )}
      {layout !== "none" && (
        <>
          <Slider label="Größe des Logos" value={design.logoSize ?? LOGO_SIZE.fallback} limits={LOGO_SIZE} step={10} show={(n) => `${n} %`} onChange={(v) => change((d) => ({ ...d, logoSize: v }))} testid="logo-size" />
          <p className="hint">In Druck, PDF und Word; auf dem Einlegeblatt halb so groß. Im Netz bleibt das Logo, wie es ist.</p>
        </>
      )}
      <Slider label={side ? "Breite des Texts neben dem Logo" : "Breite des Texts"} value={design.headWidth} limits={HEAD_WIDTH} step={2} show={(n) => `${n} %`} onChange={(v) => change((d) => ({ ...d, headWidth: v }))} testid="design-head-width" />
    </div>
  );
}

/** Points as millimetres, for the widths of the columns. */
const ptMm = (n: number) => `${Math.round((n * 25.4) / 72)} mm`;

/**
 * „Schriften, Farben, Abstände“ (docs/08-entscheidungen.md, S15) inside step 2 of the editor (S17):
 * the style the menu uses – template, logo, paper, head and sections, columns, the net, every part
 * of a menu with its font, size, colour …; at the bottom the logo's picture and the Word template.
 * The editor keeps the styles and saves them with the menu („Speichern“ at the top).
 */
/** What can be open in the list of parts: a style, or the spacing or the columns of the dishes. */
export type OpenPart = StyleKey | `drink:${DrinkStyleKey}` | `setdrink:${SetDrinkStyleKey}` | "head" | "dishSpace" | "lineSpacing" | "columns" | "rule" | "web" | "setLayout" | "translation";

export function DesignPanel({ design, card = null, change, usePreset, open, setOpen, canEdit, style, styles, sections = true, numbers = true, drinks = false, setDrinks = false, sets = false, subsections = false, heads = false, bilingual = false }: { design: Design; card?: Card | null; change: (fn: (d: Design) => Design) => void; usePreset: (p: PresetId) => void; open: OpenPart | null; setOpen: (k: OpenPart | null) => void; canEdit: boolean; style?: { id: string; name: string }; styles?: { id: string; name: string }[]; sections?: boolean; numbers?: boolean; drinks?: boolean; setDrinks?: boolean; sets?: boolean; subsections?: boolean; heads?: boolean; bilingual?: boolean }) {
  const data = useStore().data!;
  const setStyle = (k: StyleKey, c: Partial<TextStyle>) => change((d) => ({ ...d, styles: { ...d.styles, [k]: { ...d.styles[k], ...c } } }));
  const setDrinkStyle = (k: DrinkStyleKey, c: Partial<TextStyle>) => change((d) => (d.drinkStyles ? { ...d, drinkStyles: { ...d.drinkStyles, [k]: { ...d.drinkStyles[k], ...c } } } : d));
  const setSetDrinkStyle = (k: SetDrinkStyleKey, c: Partial<TextStyle>) => change((d) => (d.setDrinkStyles ? { ...d, setDrinkStyles: { ...d.setDrinkStyles, [k]: { ...d.setDrinkStyles[k], ...c } } } : d));
  /**
   * „Unterschiedliche Formatierung für Getränke im Menü“ (S53): set, they begin with the drinks' look;
   * taken away, they look as the drinks again – asked which, when they differ.
   */
  const ownSetDrinks = (on: boolean) => {
    if (on) return change((d) => ({ ...d, setDrinkStyles: Object.fromEntries(SET_DRINK_STYLE_KEYS.map((k) => [k, { ...drinkDesign(d).styles[k] }])) as Record<SetDrinkStyleKey, TextStyle> }));
    if (setDrinkStylesSame(design) || confirm("Getränke im Menü sind anders formatiert als die Getränke. OK: wieder wie die Getränke. Abbrechen: alles bleibt, wie es ist.")) change((d) => withoutSetDrinks(d));
  };
  /**
   * „Unterschiedliche Formatierung für Speisen und Getränke“ (S22): set, the drinks begin with the
   * food's look; taken away, the two are one again – asked which, when they differ.
   */
  const ownDrinks = (on: boolean) => {
    if (on) return change((d) => ({ ...d, drinkStyles: Object.fromEntries(DRINK_STYLE_KEYS.map((k) => [k, { ...d.styles[k] }])) as Record<DrinkStyleKey, TextStyle>, drinkEuro: d.euro }));
    if (drinkStylesSame(design)) return change((d) => withoutDrinks(d));
    const food = confirm("Speisen und Getränke sind unterschiedlich formatiert. OK: die Formatierung der Speisen für beide. Abbrechen: eine andere Wahl.");
    if (food) return change((d) => withoutDrinks(d));
    if (confirm("Die Formatierung der Getränke für beide übernehmen? Abbrechen: alles bleibt, wie es ist.")) change((d) => ({ ...withoutDrinks(d), styles: { ...d.styles, ...d.drinkStyles }, euro: drinkEuroOf(d) }));
  };
  return (
    <div className="design-side">
      <style>{FACES}</style>
      <fieldset disabled={!canEdit} className="design-fields">
          {/* A menu at its own size (T14): the values here are the style's, the menu's beside them. */}
          {card && (
            <p className="hint card-scale" data-testid="card-scale">
              Diese Speisekarte steht auf <b>{card.says}</b> (Größe dieser Speisekarte). Hier stehen die Werte des Stils für alle Karten mit ihm; was diese Karte setzt, steht hinter dem Pfeil: 11,5 → 16,1 pt.
            </p>
          )}
          <h3>Vorlage</h3>
          <div className="presets">
            {PRESETS.map((p) => (
              <button key={p.id} className={design.preset === p.id ? "active" : ""} onClick={() => usePreset(p.id as PresetId)} data-testid={`preset-${p.id}`}>
                <span style={cssOf({ ...p.design.styles.house, size: 13 })}>{p.label}</span>
                <small style={{ fontFamily: fontOf(p.design.styles.dish.font).css }}>{fontOf(p.design.styles.dish.font).label}</small>
              </button>
            ))}
          </div>

          <h3>Papier und Ränder</h3>
          <div className="design-page">
            <div className="row">
              <span>Format</span>
              <span className="segmented small">
                {PAGE_FORMATS.map((f) => (
                  <button key={f.id} className={design.page.format === f.id ? "active" : ""} onClick={() => change((d) => ({ ...d, page: { ...d.page, format: f.id } }))} data-testid={`format-${f.id}`}>
                    {f.label}
                  </button>
                ))}
              </span>
            </div>
            <Slider label="Rand links und rechts" value={design.page.marginX} here={card?.design.page.marginX} limits={MARGIN_X} step={1} show={mmText} onChange={(v) => change((d) => ({ ...d, page: { ...d.page, marginX: v } }))} testid="design-margin-x" />
            <Slider label="Rand oben und unten" value={design.page.marginY} here={card?.design.page.top} hereLabel="oben auf dieser Karte" limits={MARGIN_Y} step={1} show={mmText} onChange={(v) => change((d) => ({ ...d, page: { ...d.page, marginY: v } }))} testid="design-margin-y" />
          </div>

          <p className="more-legend muted small" data-testid="more-legend">
            <span className="more-dot" aria-hidden="true" /> Einstellungen, die über die Textformatierung hinausgehen
          </p>
          {/* Sections only for a menu with sections: a list has none (the style keeps the settings for the others). */}
          {/* The group „Getränke“ only with drinks on the menu (S22), „Menüs“ only with a set menu (S32). */}
          {STYLE_GROUPS.filter((g) => (sections || g.label !== "Abschnitte") && (drinks || setDrinks || g.label !== "Getränke") && (sets || g.label !== "Menüs")).map((g) => (
            <div key={g.label}>
              <h3>{g.label}</h3>
              <div className="styles-list">
                {g.label === "Kopf" && (
                  <PartRow id="head" label="Logo- und Textanzeige" summary={headSummary(design, Boolean(data.logo))} open={open === "head"} onOpen={() => setOpen(open === "head" ? null : "head")}>
                    <HeadChoice design={design} change={change} hasLogo={Boolean(data.logo)} />
                    {/* The logo in the foot of the pages (S43): in the same entry, so a plain business sees no new one (S38). */}
                    {data.logo && <FootLogoChoice design={design} change={change} />}
                  </PartRow>
                )}
                {g.label === "Abschnitte" && (
                  <PartRow id="rule" label="Linie unter jedem Abschnitt" summary={design.rule ? "an" : "aus"} open={open === "rule"} onOpen={() => setOpen(open === "rule" ? null : "rule")}>
                    <label className="check">
                      <input type="checkbox" checked={design.rule} onChange={(e) => change((d) => ({ ...d, rule: e.target.checked }))} data-testid="design-rule" />
                      Eine feine Linie unter dem Titel jedes Abschnitts
                    </label>
                  </PartRow>
                )}
                {/* How set menus look (S32 § 12): courses indented, as a column, or the menu in the middle. */}
                {g.label === "Menüs" && (
                  <PartRow id="setLayout" label="Darstellung der Menüs" summary={`${SET_LAYOUTS.find((l) => l.id === design.setLayout)?.label ?? ""} · „${design.choiceText}“`} open={open === "setLayout"} onOpen={() => setOpen(open === "setLayout" ? null : "setLayout")}>
                    <div className="layouts">
                      {SET_LAYOUTS.map((l) => (
                        <button key={l.id} className={design.setLayout === l.id ? "active" : ""} onClick={() => change((d) => ({ ...d, setLayout: l.id }))} data-testid={`set-layout-${l.id}`}>
                          <b>{l.label}</b> <span className="hint">{l.hint}</span>
                        </button>
                      ))}
                    </div>
                    <label className="row">
                      <span>Hinter einem Gang zur Wahl</span>
                      <input value={design.choiceText} onChange={(e) => change((d) => ({ ...d, choiceText: e.target.value.slice(0, CHOICE_TEXT.max) }))} onBlur={() => change((d) => ({ ...d, choiceText: choiceTextOf(d.choiceText) }))} placeholder="zur Wahl" maxLength={CHOICE_TEXT.max} data-testid="design-choice-text" />
                    </label>
                    <p className="hint">Word setzt Menüs immer mit eingerückten Gängen, die QR-Code-Speisekarte auch.</p>
                  </PartRow>
                )}
                {g.label === "Speisen" && (
                  <>
                    <PartRow id="dishSpace" label="Abstand zwischen Speisen" summary={between(design.dishSpace, card?.design.dishSpace, pt)} open={open === "dishSpace"} onOpen={() => setOpen(open === "dishSpace" ? null : "dishSpace")}>
                      <Slider label="Abstand" value={design.dishSpace} here={card?.design.dishSpace} limits={DISH_SPACE} onChange={(v) => change((d) => ({ ...d, dishSpace: v }))} testid="design-dish-space" />
                    </PartRow>
                    <PartRow id="lineSpacing" label="Zeilenabstand" summary={lineSpacingOf(design).label} open={open === "lineSpacing"} onOpen={() => setOpen(open === "lineSpacing" ? null : "lineSpacing")}>
                      <div className="row">
                        <span>Zeilen einer Speise</span>
                        <span className="segmented small">
                          {LINE_SPACINGS.map((l) => (
                            <button key={l.id} className={lineSpacingOf(design).id === l.id ? "active" : ""} onClick={() => change((d) => ({ ...d, lineSpacing: l.id }))} data-testid={`line-spacing-${l.id}`}>
                              {l.label}
                            </button>
                          ))}
                        </span>
                      </div>
                      <p className="hint">Wie eng Name, Beschreibung, Preiszeilen und Hinweis einer Speise untereinander stehen. Der Abstand zwischen den Speisen bleibt, wie er ist.</p>
                    </PartRow>
                    <PartRow id="columns" label="Spalten der Speisen" summary={`${numbers ? `Nummer ${between(design.columns.number, card?.design.columns.number, ptMm)} · ` : ""}Preis ${between(design.columns.price, card?.design.columns.price, ptMm)}`} open={open === "columns"} onOpen={() => setOpen(open === "columns" ? null : "columns")}>
                      <Columns design={design} numbers={numbers} />
                      {numbers && <Slider label="Nummer" value={design.columns.number} here={card?.design.columns.number} limits={NUMBER_COLUMN} step={1} show={ptMm} onChange={(v) => change((d) => ({ ...d, columns: { ...d.columns, number: v } }))} testid="design-col-number" />}
                      <Slider label="Preis" value={design.columns.price} here={card?.design.columns.price} limits={PRICE_COLUMN} step={1} show={ptMm} onChange={(v) => change((d) => ({ ...d, columns: { ...d.columns, price: v } }))} testid="design-col-price" />
                    </PartRow>
                  </>
                )}
                {g.label === "Getränke" && (
                  <>
                    <label className="check own-drinks">
                      <input type="checkbox" checked={Boolean(design.drinkStyles)} onChange={(e) => ownDrinks(e.target.checked)} data-testid="design-own-drinks" />
                      Unterschiedliche Formatierung für Speisen und Getränke
                    </label>
                    {design.drinkStyles &&
                      DRINK_ORDER.filter((k) => numbers || k !== "number").map((k) => (
                        <StyleRow key={`drink-${k}`} k={k} label={DRINK_LABELS[k]} testid={`style-drink-${k}`} style={design.drinkStyles![k]} here={card?.design.drinkStyles?.[k]} open={open === `drink:${k}`} onOpen={() => setOpen(open === `drink:${k}` ? null : `drink:${k}`)} onChange={(c) => setDrinkStyle(k, c)} extra={k === "price" ? (drinkEuroOf(design) ? "mit €" : "ohne €") : null}>
                          {/* The drinks' „€“ of their own (Martin, 28.10.2026). */}
                          {k === "price" && (
                            <label className="check">
                              <input type="checkbox" checked={drinkEuroOf(design)} onChange={(e) => change((d) => ({ ...d, drinkEuro: e.target.checked }))} data-testid="design-drink-euro" />
                              €-Zeichen hinter jedem Preis
                            </label>
                          )}
                        </StyleRow>
                      ))}
                    {/* Drinks in a set menu (S53): only with one on the menu; coupled to the drinks until set apart. */}
                    {setDrinks && (
                      <div className={`set-drinks ${open?.startsWith("setdrink:") ? "open" : ""}`} data-testid="set-drinks-group">
                        <p className="group-sub">Getränke im Menü</p>
                        <label className="check own-drinks">
                          <input type="checkbox" checked={Boolean(design.setDrinkStyles)} onChange={(e) => ownSetDrinks(e.target.checked)} data-testid="design-own-set-drinks" />
                          Unterschiedliche Formatierung für Getränke im Menü
                        </label>
                        {!design.setDrinkStyles && <p className="hint">Ein Getränk unter einem Gang sieht aus wie die Getränke{design.drinkStyles ? "" : ", und die wie die Speisen"}.</p>}
                        {design.setDrinkStyles &&
                          SET_DRINK_ORDER.map((k) => (
                            <StyleRow key={`setdrink-${k}`} k={k} label={SET_DRINK_LABELS[k]} testid={`style-setdrink-${k}`} style={design.setDrinkStyles![k]} here={card?.design.setDrinkStyles?.[k]} open={open === `setdrink:${k}`} onOpen={() => setOpen(open === `setdrink:${k}` ? null : `setdrink:${k}`)} onChange={(c) => setSetDrinkStyle(k, c)} />
                          ))}
                      </div>
                    )}
                  </>
                )}
                {/* Without numbers on the menu (step 1) nothing of them here. */}
                {/* „Unterabschnitt“ (S44) only with a subsection on the menu, „Mengen über den Preisen“ (S46) only with such a head or drinks. */}
                {g.keys.filter((k) => (numbers || k !== "number") && (subsections || k !== "subsection") && (heads || k !== "sizeHead") && (drinks || k !== "part")).map((k) => (
                  <Fragment key={k}>
                  <StyleRow k={k} style={design.styles[k]} here={card?.design.styles[k]} open={open === k} onOpen={() => setOpen(open === k ? null : k)} onChange={(c) => setStyle(k, c)} extra={extraOf(k, design, card?.design)}>
                    {/* „Satz der Speisen“ (S48): the price column at the right, or everything in the middle. */}
                    {k === "dish" && (
                      <div className="row">
                        <span>Satz der Speisen</span>
                        <span className="segmented small">
                          <button className={design.align === "center" ? "" : "active"} onClick={() => change((d) => (({ align: _a, ...rest }) => rest)(d))} data-testid="design-align-right">
                            Preise rechts
                          </button>
                          <button className={design.align === "center" ? "active" : ""} onClick={() => change((d) => ({ ...d, align: "center" }))} data-testid="design-align-center">
                            mittig
                          </button>
                        </span>
                      </div>
                    )}
                    {k === "section" && <Slider label="Abstand vor einem Abschnitt" value={design.sectionSpace} here={card?.design.sectionSpace} limits={SECTION_SPACE} onChange={(v) => change((d) => ({ ...d, sectionSpace: v }))} testid="design-section-space" />}
                    {k === "orLine" && (
                      <>
                        <label className="row">
                          <span>Wort</span>
                          <input value={design.orText} onChange={(e) => change((d) => ({ ...d, orText: e.target.value.slice(0, OR_TEXT.max) }))} onBlur={() => change((d) => ({ ...d, orText: orTextOf(d.orText) }))} placeholder="oder" maxLength={OR_TEXT.max} data-testid="design-or-text" />
                        </label>
                        {/* „Zwischen den Gängen“ (S57) where its look is, and where a tap on it in the PDF lands (Martin, 02.11.2026). */}
                        <label className="row">
                          <span>Zwischen den Gängen</span>
                          <select
                            value={design.courseMark ?? ""}
                            onChange={(e) =>
                              change((d) => {
                                const { courseMark: _, ...rest } = d;
                                return e.target.value ? { ...rest, courseMark: e.target.value as CourseMark } : rest;
                              })
                            }
                            data-testid="design-course-mark"
                          >
                            <option value="">nichts, nur Abstand</option>
                            {COURSE_MARKS.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.label} ({m.hint})
                              </option>
                            ))}
                          </select>
                        </label>
                      </>
                    )}
                    {k === "variant" && <Slider label="Einzug der Preiszeilen" value={design.variantIndent} here={card?.design.variantIndent} limits={VARIANT_INDENT} step={1} show={ptMm} onChange={(v) => change((d) => ({ ...d, variantIndent: v }))} testid="design-variant-indent" />}
                    {k === "price" && (
                      <label className="check">
                        <input type="checkbox" checked={design.euro} onChange={(e) => change((d) => ({ ...d, euro: e.target.checked }))} data-testid="design-euro" />
                        €-Zeichen hinter jedem Preis
                      </label>
                    )}
                  </StyleRow>
                  {/* „Übersetzung“ (S60): only with a bilingual card in view, below the description it follows. */}
                  {k === "description" && bilingual && (
                    <PartRow id="translation" label="Übersetzung" summary={translationSummary(translationOf(design))} open={open === "translation"} onOpen={() => setOpen(open === "translation" ? null : "translation")}>
                      <TranslationChoice design={design} change={change} />
                    </PartRow>
                  )}
                  </Fragment>
                ))}
              </div>
            </div>
          ))}
          {/* The menu on the net in this style too (S15); lost in the accordion (24.10.2026), back on 25.10.2026. */}
          <div>
            <h3>QR-Code-Speisekarte</h3>
            <div className="styles-list">
              <PartRow id="web" label="Auch für die QR-Code-Speisekarte" summary={design.web ? "an" : "aus"} open={open === "web"} onOpen={() => setOpen(open === "web" ? null : "web")}>
                <label className="check">
                  <input type="checkbox" checked={design.web} onChange={(e) => change((d) => ({ ...d, web: e.target.checked }))} data-testid="design-web" />
                  Die QR-Code-Speisekarte in diesem Stil
                </label>
                <p className="hint">Schriften, Farben, fett und kursiv kommen aufs Handy, die Größen passend dazu. Sichtbar nach „Änderungen übernehmen“; bei dunkler Anzeige bleiben die Farben der Seite. Aus: die QR-Code-Speisekarte in ihrem eigenen, schlichten Aussehen.</p>
              </PartRow>
            </div>
          </div>
      </fieldset>
      {canEdit && (
        <>
          <h3>Logo ändern</h3>
          <div className="design-page">
            <LogoPicture />
          </div>
          <h3>Word</h3>
          {style && <WordStyles style={style} styles={styles ?? [style]} />}
        </>
      )}
    </div>
  );
}


/**
 * The business's logo (S13), one picture for every style: upload (PNG, JPG, SVG; made into a PNG
 * without its empty edge) or delete, at once on the server. On the net after „Neu veröffentlichen“.
 */
export function LogoPicture() {
  const data = useStore().data!;
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  const logo = heldLogo(data);
  async function upload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setState({ busy: true });
    try {
      const png = await logoPng(file);
      if ("error" in png) return setState({ error: png.error });
      await store.change(() => api("/api/logo", { method: "PUT", body: { data: png.data, file: file.name } }));
      setState({ ok: data.published ? "Logo übernommen. Im Netz erscheint es nach „Neu veröffentlichen“." : "Logo übernommen." });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  async function remove() {
    if (!confirm("Das Logo löschen? Auf den Speisekarten steht dann wieder nur der Name.")) return;
    try {
      await store.change(() => api("/api/logo", { method: "DELETE" }));
      setState({ ok: "Logo gelöscht" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <div className="logo-picture">
      {data.logo && (
        <div className="logo-preview" data-testid="logo-preview">
          {logo ? <img src={`data:image/png;base64,${logo.data}`} alt={`Logo von ${data.settings.house || data.workspace}`} /> : <span className="muted">Lädt …</span>}
        </div>
      )}
      <div className="buttons">
        <label className="upload">
          <span className="button">{state.busy ? "Wird übernommen …" : data.logo ? "Anderes Logo …" : "Logo hochladen …"}</span>
          <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,.svg" onChange={(e) => void upload(e)} hidden data-testid="logo-upload" />
        </label>
        {data.logo && (
          <button className="danger" onClick={() => void remove()} data-testid="logo-remove">
            Löschen
          </button>
        )}
      </div>
      {!data.logo && <p className="hint">PNG, JPG oder SVG; einen weißen Rand schneidet die App ab.</p>}
      {state.error && <p className="error-box">{state.error}</p>}
      {state.ok && <p className="ok-text">{state.ok}</p>}
    </div>
  );
}

/** The three columns of a dish, to scale on the paper of the style: number, dish, price. */
function Columns({ design, numbers }: { design: Design; numbers: boolean }) {
  const paper = PAGE_FORMATS.find((f) => f.id === design.page.format)!;
  const width = paper.width - 2 * ((design.page.marginX * 72) / 25.4);
  const pct = (v: number) => `${Math.max(0, (v / width) * 100)}%`;
  // The number only when the menu shows numbers (step 1); else two columns, the dish and its price.
  const no = numbers ? design.columns.number : 0;
  const dish = width - no - design.columns.price;
  return (
    <div className="columns-bar" aria-hidden="true">
      {no > 0 && (
        <span className="c-no" style={{ width: pct(no) }}>
          1
        </span>
      )}
      <span className="c-dish" style={{ width: pct(dish) }}>
        Speise und Beschreibung
      </span>
      <span className="c-pr" style={{ width: pct(design.columns.price) }}>
        {design.euro ? "6,80 €" : "6,80"}
      </span>
    </div>
  );
}

/** The style of a menu at its own size (T14), and what that size is, said. */
export type Card = { design: Design; says: string };

/** Values differ enough to say so (a tenth of a point or millimetre). */
const differs = (a: number, b: number | undefined): b is number => b !== undefined && Math.abs(a - b) > 0.05;
/**
 * A value with what this menu sets instead, short enough to leave the part's name its room (Martin,
 * 28.10.2026: „Überschrift „Ge…“ was cut): „7,2 → 14,4 pt“.
 */
const between = (value: number, here: number | undefined, show: (v: number) => string) => (differs(value, here) ? `${show(value).replace(/ (pt|mm)$/, "")}\u00a0→\u00a0${show(here).replace(" ", "\u00a0")}` : show(value).replace(" ", "\u00a0"));

function Slider({ label, value, here, hereLabel = "auf dieser Karte", limits, onChange, testid, step = 0.5, show = pt }: { label: string; value: number; here?: number; hereLabel?: string; limits: { min: number; max: number }; onChange: (v: number) => void; testid: string; step?: number; show?: (v: number) => string }) {
  return (
    <label className="slider">
      <span>
        {label} <b>{show(value)}</b>
        {differs(value, here) && (
          <small className="here" data-testid={`${testid}-here`}>
            {" "}
            · {hereLabel} {show(here)}
          </small>
        )}
      </span>
      <input type="range" min={limits.min} max={limits.max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />
    </label>
  );
}

const CAPS: { value: Caps; label: string }[] = [
  { value: "normal", label: "normal" },
  { value: "upper", label: "GROSS" },
  { value: "small", label: "Kapitälchen" },
];

/** A setting of the dishes in the list of parts (spacing, columns): its name and value; open, its controls. */
/** „Übersetzung“ in a row's summary (S60): „kursiv umgekehrt“, „wie das Deutsche · eigene Farbe · 1 pt kleiner“. */
const translationSummary = (r: TranslationRule) => [r.italic === "flip" ? "kursiv umgekehrt" : "kursiv wie das Deutsche", r.color ? "eigene Farbe" : "", r.smaller ? `${String(r.smaller).replace(".", ",")} pt kleiner` : ""].filter(Boolean).join(" · ");

/**
 * „Übersetzung“ on a bilingual card (S60): a rule against the German text above it, not a style of
 * its own – it holds below the food's description, the drinks' (their own look or the food's, S22),
 * a drink's in a set menu (S53) and a text of the catalogue (S14), coupled or not.
 */
function TranslationChoice({ design, change }: { design: Design; change: (fn: (d: Design) => Design) => void }) {
  const rule = translationOf(design);
  const set = (c: Partial<TranslationRule>) =>
    change((d) => {
      const { translation: _, ...rest } = d;
      const next = { ...translationOf(d), ...c };
      // The usual rule is not stored: a style as before stays as it was.
      return next.italic === DEFAULT_TRANSLATION.italic && !next.color && !next.smaller ? rest : { ...rest, translation: next };
    });
  return (
    <>
      <div className="row">
        <span>Kursiv</span>
        <span className="segmented small">
          <button className={rule.italic === "flip" ? "active" : ""} onClick={() => set({ italic: "flip" })} data-testid="translation-italic-flip">
            umgekehrt
          </button>
          <button className={rule.italic === "same" ? "active" : ""} onClick={() => set({ italic: "same" })} data-testid="translation-italic-same">
            wie das Deutsche
          </button>
        </span>
      </div>
      <div className="row">
        <span>Farbe</span>
        <span className="segmented small">
          <button className={rule.color ? "" : "active"} onClick={() => set({ color: null })} data-testid="translation-color-same">
            wie das Deutsche
          </button>
          <button className={rule.color ? "active" : ""} onClick={() => set({ color: rule.color ?? "#6b6b6b" })} data-testid="translation-color-own">
            eigene
          </button>
        </span>
        {rule.color && <input type="color" value={rule.color} onChange={(e) => set({ color: e.target.value })} aria-label="Farbe der Übersetzung" data-testid="translation-color" />}
      </div>
      <Slider label="Kleiner als das Deutsche um" value={rule.smaller} limits={TRANSLATION_SMALLER} step={TRANSLATION_SMALLER.step} onChange={(v) => set({ smaller: v })} testid="translation-smaller" />
      <p className="hint">Die Zeile unter Speisen, Getränken und Texten richtet sich nach dem deutschen Text darüber: Haben die Getränke einen eigenen Look, nach deren Beschreibung. Ist die Beschreibung kursiv, steht die Übersetzung aufrecht, und umgekehrt.</p>
    </>
  );
}

export function PartRow({ id, label, summary, open, onOpen, children }: { id: string; label: string; summary: ReactNode; open: boolean; onOpen: () => void; children: ReactNode }) {
  return (
    <div className={`style-row ${open ? "open" : ""}`} data-testid={`part-${id}`}>
      <button className="style-head" onClick={onOpen} aria-expanded={open}>
        <span className="sample">
          <span className="more-dot" title="Einstellungen, die über die Textformatierung hinausgehen" aria-hidden="true" />
          {label}
        </span>
        <span className="more-value">{summary}</span>
      </button>
      {open && <div className="style-controls">{children}</div>}
    </div>
  );
}

/**
 * A list as long as fits on its line, in whole entries: „vegan, vegetarisch, …“ (Martin, 25.10.2026),
 * never cut in the middle of a word. Measured after drawing and again when the line gets wider or narrower.
 */
export function FitList({ items, empty = "keine" }: { items: string[]; empty?: string }) {
  const box = useRef<HTMLSpanElement>(null);
  const [n, setN] = useState(items.length);
  const key = items.join("\u0000");
  // Anew from all entries when they change or the line's width does.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const all = () => setN(items.length);
    all();
    const ro = new ResizeObserver(all);
    // The whole line, whose width does not follow its text (the value's own would, and swing).
    ro.observe(el.closest(".style-head") ?? el.parentElement ?? el);
    return () => ro.disconnect();
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  // One fewer while it does not fit.
  useLayoutEffect(() => {
    const el = box.current;
    if (el && n > 1 && el.scrollWidth > el.clientWidth + 1) setN(n - 1);
  });
  const text = items.length === 0 ? empty : `${items.slice(0, n).join(", ")}${n < items.length ? ", …" : ""}`;
  return (
    <span ref={box} className="fit-list" title={items.join(", ")}>
      {text}
    </span>
  );
}

/** What a part's entry holds besides its text's look, as said when it is closed; null: nothing else. */
function extraOf(k: StyleKey, d: Design, card?: Design): string | null {
  if (k === "section") return `Abstand ${between(d.sectionSpace, card?.sectionSpace, pt)}`;
  if (k === "variant") return `Einzug ${between(d.variantIndent, card?.variantIndent, ptMm)}`;
  if (k === "price") return d.euro ? "mit €" : "ohne €";
  if (k === "orLine") return `„${d.orText}“${d.courseMark ? ` · ${COURSE_MARKS.find((m) => m.id === d.courseMark)?.label ?? ""}` : ""}`;
  // Said only when so (S48): the plain business's row stays as it was (S38).
  if (k === "dish") return d.align === "center" ? "mittig" : null;
  return null;
}

/**
 * One part of the menu: its name in its own style; open, everything to change, and what belongs to
 * it (`children`). An entry with more than the look of a text (`extra`) has the dot and says it closed.
 */
function StyleRow({ k, style, here, open, onOpen, onChange, children, extra = null, label, testid }: { k: StyleKey; style: TextStyle; here?: TextStyle; open: boolean; onOpen: () => void; onChange: (c: Partial<TextStyle>) => void; children?: ReactNode; extra?: string | null; label?: string; testid?: string }) {
  // Broken only between its parts, never inside „22,4 → 22,8 pt“ or „EB Garamond“.
  const summary = [fontOf(style.font).label.replace(/ /g, "\u00a0"), between(style.size, here?.size, pt), style.bold ? "fett" : "", style.italic ? "kursiv" : ""].filter(Boolean).join("\u00a0· ");
  const swatch = DESIGN_COLORS.some((c) => c.hex === style.color);
  return (
    <div className={`style-row ${open ? "open" : ""}`} data-testid={testid ?? `style-${k}`}>
      <button className="style-head" onClick={onOpen} aria-expanded={open}>
        <span className="sample" style={cssOf(style)}>
          {extra !== null && <span className="more-dot" title="Einstellungen, die über die Textformatierung hinausgehen" aria-hidden="true" />}
          {label ?? STYLE_LABELS[k]}
        </span>
        <span className="muted">
          {extra !== null && <span className="more-value">{extra} · </span>}
          {summary}
        </span>
      </button>
      {open && (
        <div className="style-controls">
          <div className="row">
            <select
              value={style.font}
              onChange={(e) => {
                const font = e.target.value as TextStyle["font"];
                // An ornamental font (S56): no capitals, bold and italic only where it has them.
                const cuts = fontCuts(font);
                onChange(isDisplayFont(font) ? { font, caps: "normal", bold: style.bold && cuts.bold, italic: style.italic && cuts.italic } : { font });
              }}
              aria-label="Schrift"
              data-testid="style-font"
            >
              <optgroup label="mit Serifen">
                {DESIGN_FONTS.filter((f) => f.kind === "serif").map((f) => (
                  <option key={f.id} value={f.id} style={{ fontFamily: f.css }}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
              <optgroup label="ohne Serifen">
                {DESIGN_FONTS.filter((f) => f.kind === "sans").map((f) => (
                  <option key={f.id} value={f.id} style={{ fontFamily: f.css }}>
                    {f.label}
                  </option>
                ))}
              </optgroup>
              {DISPLAY_KEYS.includes(k) && (
                <optgroup label="Schmuckschrift, nur für Überschriften">
                  {DESIGN_FONTS.filter((f) => f.kind === "display").map((f) => (
                    <option key={f.id} value={f.id} style={{ fontFamily: f.css }}>
                      {f.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <span className="stepper">
              <button onClick={() => onChange({ size: Math.max(SIZE_LIMITS.min, style.size - 0.5) })} aria-label="kleiner">
                −
              </button>
              <input
                type="number"
                min={SIZE_LIMITS.min}
                max={SIZE_LIMITS.max}
                step={0.5}
                value={style.size}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v >= SIZE_LIMITS.min && v <= SIZE_LIMITS.max) onChange({ size: v });
                }}
                aria-label="Größe in Punkt"
                data-testid="style-size"
              />
              <button onClick={() => onChange({ size: Math.min(SIZE_LIMITS.max, style.size + 0.5) })} aria-label="größer">
                +
              </button>
            </span>
          </div>
          <div className="row toggles">
            <button className={style.bold ? "active" : ""} onClick={() => onChange({ bold: !style.bold })} aria-pressed={style.bold} disabled={!fontCuts(style.font).bold} title={fontCuts(style.font).bold ? undefined : "Diese Schrift hat keinen fetten Schnitt"} data-testid="style-bold">
              <b>F</b> fett
            </button>
            <button className={style.italic ? "active" : ""} onClick={() => onChange({ italic: !style.italic })} aria-pressed={style.italic} disabled={!fontCuts(style.font).italic} title={fontCuts(style.font).italic ? undefined : "Diese Schrift hat keinen kursiven Schnitt"} data-testid="style-italic">
              <i>K</i> kursiv
            </button>
            <select value={style.caps} onChange={(e) => onChange({ caps: e.target.value as Caps })} aria-label="Schreibweise" disabled={isDisplayFont(style.font)} title={isDisplayFont(style.font) ? "Schmuckschriften stehen nie in Großbuchstaben" : undefined} data-testid="style-caps">
              {CAPS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <select value={TRACKINGS.some((t) => t.value === style.tracking) ? style.tracking : ""} onChange={(e) => onChange({ tracking: Number(e.target.value) })} aria-label="Sperrung">
              {!TRACKINGS.some((t) => t.value === style.tracking) && <option value="">Sperrung {style.tracking}</option>}
              {TRACKINGS.map((t) => (
                <option key={t.value} value={t.value}>
                  Sperrung: {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="swatches" role="radiogroup" aria-label="Farbe">
            {DESIGN_COLORS.map((c) => (
              <button key={c.hex} className={style.color === c.hex ? "active" : ""} style={{ background: c.hex }} onClick={() => onChange({ color: c.hex })} title={c.label} aria-label={c.label} role="radio" aria-checked={style.color === c.hex} />
            ))}
            <label className={`custom ${swatch ? "" : "active"}`} title="Andere Farbe">
              <input type="color" value={style.color} onChange={(e) => onChange({ color: e.target.value })} aria-label="Andere Farbe" />
            </label>
          </div>
          {children && <div className="style-extra">{children}</div>}
        </div>
      )}
    </div>
  );
}


/** What stands at a point of a page (PDF points): the smallest mark there. */
function markAt(marks: Mark[], page: number, x: number, y: number): Mark | null {
  let best: Mark | null = null;
  for (const m of marks) {
    if (m.page !== page || x < m.x - 2 || x > m.x + m.w + 2 || y < m.y - 1 || y > m.y + m.h + 1) continue;
    if (!best || m.w * m.h < best.w * best.h) best = m;
  }
  return best;
}
/** Zoom steps of the preview: 1 is the whole page in the frame. */
const ZOOMS = [1, 1.5, 2, 3, 4];
const MAX_ZOOM = 4;
/** The largest canvas drawn, in pixels across (more costs memory and adds nothing). */
const MAX_CANVAS = 4096;
/** Space around the page in the frame, in pixels. */
const PAD = 8;

/**
 * The PDF of the menu as the guests get it (pdf.js), made again a moment after the last change. A
 * page always fits whole into the frame, with no scrollbar (Martin, 24.10.2026); „+“ and „−“, a
 * pinch or ⌘ with the wheel zoom in, dragging moves the page, the small overview at the corner
 * shows and moves what is in view. Over the page lie the places of its parts: the open style is
 * outlined, a tap opens the style of a place.
 */
/** The entry a mark opens: a drink's own style (S22) or the style of its part. */
const partOfMark = (mk: Mark): OpenPart => (mk.translation ? "translation" : mk.setDrink ? `setdrink:${mk.key as SetDrinkStyleKey}` : mk.drink ? `drink:${mk.key as DrinkStyleKey}` : mk.key);

export function PdfPreview({ menu, design, id, open = null, onPick, lang = null }: { menu: MenuInput; design: Design; id?: string; open?: OpenPart | null; onPick?: (k: OpenPart) => void; lang?: CardLang | null }) {
  const data = useStore().data!;
  const frame = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ busy: boolean; pages: number; error?: string }>({ busy: true, pages: 0 });
  const [made, setMade] = useState<{ doc: PDFDocumentProxy; task: PDFDocumentLoadingTask; marks: Mark[]; page: { w: number; h: number } } | null>(null);
  const [hover, setHover] = useState<Mark | null>(null);
  const [pageNo, setPageNo] = useState(0);
  const [zoom, setZoom] = useState(1);
  /** Where the page's top left corner stands in the frame, in pixels. */
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [thumb, setThumb] = useState<string | null>(null);
  const logo = heldLogo(data);
  const m = useMemo(() => printMenuOf(menu, data, new Date(), design, id, lang), [menu, data, design, logo, id, lang]);

  // The page's size in the frame: whole at zoom 1.
  const pw = made?.page.w ?? 595.28;
  const ph = made?.page.h ?? 841.89;
  const fit = size.w > 0 ? Math.max(0.05, Math.min((size.w - 2 * PAD) / pw, (size.h - 2 * PAD) / ph)) : 0;
  const W = pw * fit * zoom;
  const H = ph * fit * zoom;
  /** An offset that keeps the page in view: centred where it is smaller than the frame. */
  const clamp = (o: { x: number; y: number }, w = W, h = H) => ({
    x: w <= size.w ? (size.w - w) / 2 : Math.min(0, Math.max(size.w - w, o.x)),
    y: h <= size.h ? (size.h - h) / 2 : Math.min(0, Math.max(size.h - h, o.y)),
  });
  const pos = clamp(off);
  // For the listeners that are not React's (the wheel).
  const view = useRef({ zoom, pos, W, H, size });
  view.current = { zoom, pos, W, H, size };

  // The frame's size, followed.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Beside the settings (wide screen) the preview sticks at the top; it is as high as what is left of
  // the window above the tabs, also before it sticks, so the page is never cut by the window.
  useEffect(() => {
    const box = frame.current?.closest<HTMLElement>(".design-preview");
    if (!box) return;
    let raf = 0;
    const fit = () => {
      raf = 0;
      if (getComputedStyle(box).position !== "sticky") return void [box.style.removeProperty("height"), box.style.removeProperty("top")];
      // It sticks below the editor's bar („Speichern“), which sticks too.
      const bar = document.querySelector<HTMLElement>(".editor > .toolbar");
      const stick = bar && getComputedStyle(bar).position === "sticky" ? (parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight + 8 : parseFloat(getComputedStyle(box).top) || 0;
      box.style.top = `${stick}px`;
      const top = Math.max(box.getBoundingClientRect().top, stick);
      const tabs = document.querySelector("nav.tabs")?.getBoundingClientRect().height ?? 0;
      box.style.height = `${Math.max(240, window.innerHeight - top - tabs - 12)}px`;
    };
    const later = () => void (raf ||= requestAnimationFrame(fit));
    fit();
    window.addEventListener("scroll", later, { passive: true });
    window.addEventListener("resize", later);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", later);
      window.removeEventListener("resize", later);
    };
  }, []);

  // The PDF, a moment after the last change.
  useEffect(() => {
    let gone = false;
    setState((s) => ({ ...s, busy: true }));
    const timer = setTimeout(async () => {
      try {
        const [{ markedPdf }, pdfjs, worker] = await Promise.all([import("../menuPdf.ts"), import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        const pdf = await markedPdf(m);
        if (gone) return;
        const task = pdfjs.getDocument({ data: pdf.bytes });
        const doc = await task.promise;
        if (gone) return void task.destroy();
        setMade((before) => {
          if (before) void before.task.destroy();
          return { doc, task, marks: pdf.marks, page: pdf.page };
        });
        setPageNo((n) => Math.min(n, doc.numPages - 1));
        setState({ busy: false, pages: doc.numPages });
      } catch (err) {
        if (!gone) setState({ busy: false, pages: 0, error: messageOf(err) });
      }
    }, 250);
    return () => {
      gone = true;
      clearTimeout(timer);
    };
  }, [m]);
  const last = useRef(made);
  last.current = made;
  useEffect(() => () => void last.current?.task.destroy(), []);

  // The page drawn as large as it is shown (sharp when zoomed in); the old one stays until the new one is there.
  const drawW = Math.round(W);
  useEffect(() => {
    if (!made || drawW <= 0) return;
    let task: { cancel: () => void } | null = null;
    let gone = false;
    const timer = setTimeout(async () => {
      try {
        const page = await made.doc.getPage(pageNo + 1);
        const scale = Math.min((drawW * (window.devicePixelRatio || 1)) / pw, MAX_CANVAS / pw);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.className = "pdf-page";
        canvas.setAttribute("data-testid", "pdf-page");
        canvas.dataset.page = String(pageNo);
        const render = page.render({ canvas, viewport });
        task = render;
        await render.promise;
        if (gone) return;
        stage.current?.querySelector(".pdf-canvas")?.replaceChildren(canvas);
        // The overview: the same page, small.
        const small = document.createElement("canvas");
        small.width = 120;
        small.height = Math.round((120 * ph) / pw);
        small.getContext("2d")?.drawImage(canvas, 0, 0, small.width, small.height);
        setThumb(small.toDataURL());
      } catch {
        // cancelled by a newer drawing
      }
    }, 80);
    return () => {
      gone = true;
      clearTimeout(timer);
      task?.cancel();
    };
  }, [made, pageNo, drawW]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Zoom to `z`, keeping the point at (cx, cy) of the frame where it is. */
  const zoomTo = (z: number, cx = size.w / 2, cy = size.h / 2) => {
    const next = Math.min(MAX_ZOOM, Math.max(1, z));
    const fx = (cx - pos.x) / W;
    const fy = (cy - pos.y) / H;
    const w = (W / zoom) * next;
    const h = (H / zoom) * next;
    setZoom(next);
    setOff(clamp({ x: cx - fx * w, y: cy - fy * h }, w, h));
  };
  /** The next step of ZOOMS up or down from where it is (a pinch may have left it between two). */
  const step = (by: 1 | -1) => zoomTo(by > 0 ? (ZOOMS.find((z) => z > zoom + 0.01) ?? MAX_ZOOM) : ([...ZOOMS].reverse().find((z) => z < zoom - 0.01) ?? 1));


  // Pinch on a trackpad (the browser sends it as the wheel with ctrl) or ⌘/ctrl with the wheel: zoom; the wheel alone moves a page larger than the frame.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      const v = view.current;
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        zoomRef.current(v.zoom * Math.exp(-e.deltaY / 200), e.clientX - r.left, e.clientY - r.top);
      } else if (v.W > v.size.w + 1 || v.H > v.size.h + 1) {
        e.preventDefault();
        setOff({ x: v.pos.x - e.deltaX, y: v.pos.y - e.deltaY });
      }
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);
  const zoomRef = useRef(zoomTo);
  zoomRef.current = zoomTo;

  // Dragging moves the page; two fingers zoom. A drag is no tap.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ x: number; y: number; at: { x: number; y: number }; moved: boolean; pinch?: { d: number; zoom: number } } | null>(null);
  const movable = W > size.w + 1 || H > size.h + 1;
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const ps = [...pointers.current.values()];
    if (ps.length === 2) {
      drag.current = { x: 0, y: 0, at: pos, moved: true, pinch: { d: Math.hypot(ps[0]!.x - ps[1]!.x, ps[0]!.y - ps[1]!.y), zoom } };
    } else drag.current = { x: e.clientX, y: e.clientY, at: pos, moved: false };
    if (movable || ps.length === 2) e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return void (onPick && setHover(at(e)));
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const d = drag.current;
    if (!d) return;
    const ps = [...pointers.current.values()];
    if (d.pinch && ps.length === 2) {
      const r = e.currentTarget.getBoundingClientRect();
      zoomTo((d.pinch.zoom * Math.hypot(ps[0]!.x - ps[1]!.x, ps[0]!.y - ps[1]!.y)) / d.pinch.d, (ps[0]!.x + ps[1]!.x) / 2 - r.left, (ps[0]!.y + ps[1]!.y) / 2 - r.top);
      return;
    }
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.hypot(dx, dy) > 4) d.moved = true;
    if (d.moved && movable) setOff({ x: d.at.x + dx, y: d.at.y + dy });
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) setTimeout(() => (drag.current = null), 0);
  };

  /** The mark under the pointer, on the page shown. */
  const at = (e: { clientX: number; clientY: number }): Mark | null => {
    const r = stage.current?.getBoundingClientRect();
    if (!made || !r || e.clientY < r.top || e.clientY > r.bottom || e.clientX < r.left || e.clientX > r.right) return null;
    return markAt(made.marks, pageNo, ((e.clientX - r.left) / r.width) * pw, ph - ((e.clientY - r.top) / r.height) * ph);
  };

  /** Boxes over the page shown, in per cent of it. */
  const boxes = (marks: Mark[], cls: string) =>
    marks
      .filter((mk) => mk.page === pageNo)
      .map((mk, i) => <span key={`${cls}${i}`} className={cls} style={{ top: `${((ph - mk.y - mk.h) / ph) * 100}%`, left: `${(mk.x / pw) * 100}%`, width: `${(mk.w / pw) * 100}%`, height: `${(mk.h / ph) * 100}%` }} />);

  /** The overview: a tap or drag puts that point in the middle of the frame. */
  const focus = (e: PointerEvent<HTMLDivElement>) => {
    if (e.type === "pointermove" && e.buttons === 0) return;
    if (e.type === "pointerdown") e.currentTarget.setPointerCapture(e.pointerId);
    const r = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    setOff(clamp({ x: size.w / 2 - fx * W, y: size.h / 2 - fy * H }));
  };
  const seen = { left: Math.max(0, -pos.x / W), top: Math.max(0, -pos.y / H), width: Math.min(1, size.w / W), height: Math.min(1, size.h / H) };

  return (
    <div className="pdf-viewer">
      <div className="pdf-bar">
        <p className="preview-state muted" data-testid="pdf-state">
          {state.error ? `Die Vorschau ging nicht: ${state.error}` : state.busy && !made ? "PDF wird erstellt …" : `PDF, ${state.pages === 1 ? "1 Seite" : `${state.pages} Seiten`}`}
          {!state.error && onPick && <span className="hint"> · Auf eine Stelle tippen öffnet ihre Formatvorlage</span>}
        </p>
        <span className="pdf-tools">
          {state.pages > 1 && (
            <>
              <button onClick={() => [setPageNo((n) => Math.max(0, n - 1)), setOff({ x: 0, y: 0 })]} disabled={pageNo === 0} aria-label="Vorige Seite" data-testid="pdf-prev">
                ‹
              </button>
              <span className="pdf-pageno" data-testid="pdf-pageno">
                {pageNo + 1}/{state.pages}
              </span>
              <button onClick={() => [setPageNo((n) => Math.min(state.pages - 1, n + 1)), setOff({ x: 0, y: 0 })]} disabled={pageNo >= state.pages - 1} aria-label="Nächste Seite" data-testid="pdf-next">
                ›
              </button>
            </>
          )}
          <button onClick={() => step(-1)} disabled={zoom <= 1} aria-label="Verkleinern" data-testid="pdf-zoom-out">
            −
          </button>
          <button onClick={() => zoomTo(1)} disabled={zoom === 1} title="Ganze Seite" data-testid="pdf-fit">
            {Math.round(zoom * 100)} %
          </button>
          <button onClick={() => step(1)} disabled={zoom >= MAX_ZOOM} aria-label="Vergrößern" data-testid="pdf-zoom-in">
            +
          </button>
        </span>
      </div>
      <div
        ref={frame}
        className={`pdf-frame ${onPick ? "" : "still"} ${movable ? "movable" : ""}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHover(null)}
        onClick={(e) => {
          if (drag.current?.moved) return;
          const mk = at(e);
          if (mk && onPick) onPick(partOfMark(mk));
        }}
        title={hover ? (hover.setDrink ? SET_DRINK_LABELS[hover.key as SetDrinkStyleKey] : hover.drink ? DRINK_LABELS[hover.key as DrinkStyleKey] : STYLE_LABELS[hover.key]) : undefined}
        data-testid="pdf-pages"
      >
        <div ref={stage} className="pdf-stage" style={{ left: pos.x, top: pos.y, width: W, height: H }}>
          <div className="pdf-canvas" />
          {open && made && !state.busy && boxes(made.marks.filter((mk) => partOfMark(mk) === open), "mark-open")}
          {hover && !state.busy && boxes([hover], "mark-hover")}
        </div>
        {zoom > 1 && thumb && (
          <div className="pdf-overview" onPointerDown={(e) => [e.stopPropagation(), focus(e)]} onPointerMove={(e) => [e.stopPropagation(), focus(e)]} onClick={(e) => e.stopPropagation()} aria-label="Übersicht: hier tippen oder ziehen zeigt diese Stelle" data-testid="pdf-overview">
            <img src={thumb} alt="" draggable={false} />
            <span className="seen" style={{ left: `${seen.left * 100}%`, top: `${seen.top * 100}%`, width: `${seen.width * 100}%`, height: `${seen.height * 100}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}

/** The drinks' own formats (S22), named for drinks. */
const DRINK_LABELS: Record<DrinkStyleKey, string> = {
  number: "Nummer des Getränks",
  dish: "Getränk",
  description: "Beschreibung des Getränks",
  variant: "Mengen und Preise",
  price: "Preis des Getränks",
  codes: "Allergene des Getränks",
  tags: "Merkmale des Getränks",
  itemNote: "Hinweis zum Getränk",
  sizeHead: "Mengen über den Preisen der Getränke",
};

/** The drinks' formats in the order of the dishes' (S46: „Mengen über den Preisen“ after the price). */
const DRINK_ORDER: readonly DrinkStyleKey[] = STYLE_GROUPS.flatMap((g) => g.keys).filter((k): k is DrinkStyleKey => (DRINK_STYLE_KEYS as readonly string[]).includes(k));

/** The formats of drinks in a set menu (S53), named for them. */
const SET_DRINK_LABELS: Record<SetDrinkStyleKey, string> = {
  dish: "Getränk im Menü",
  description: "Beschreibung des Getränks im Menü",
  variant: "Mengen im Menü",
  price: "Preise im Menü",
  codes: "Allergene des Getränks im Menü",
  tags: "Merkmale des Getränks im Menü",
  itemNote: "Hinweis zum Getränk im Menü",
};
const SET_DRINK_ORDER: readonly SetDrinkStyleKey[] = DRINK_ORDER.filter((k): k is SetDrinkStyleKey => (SET_DRINK_STYLE_KEYS as readonly string[]).includes(k));

/** Drinks in a set menu coupled again (S53): they look as the drinks. */
function withoutSetDrinks(d: Design): Design {
  const next = { ...d };
  delete next.setDrinkStyles;
  return next;
}

/** A style coupled again (S22): the drinks without a look of their own. */
function withoutDrinks(d: Design): Design {
  const next = { ...d };
  delete next.drinkStyles;
  delete next.drinkEuro;
  return next;
}
